import styled from "styled-components";

import { ChevronIcon } from "../../../../icons/glyphs/ChevronIcon";
import { controlFocusRing, controlTransition } from "../../../foundations/controlStyles";

export type OverlayMode = "months" | "years";

export interface CalendarNavProps {
  /** 이 pane이 보여주는 달(라벨 산출). */
  paneView: Date;
  locale?: string;
  /** 월 grid가 열려 있는가(월 버튼 캐럿 ▲). */
  monthsOpen: boolean;
  /** 연 grid가 열려 있는가(연 버튼 캐럿 ▲). */
  yearsOpen: boolean;
  /** 월/연 버튼 클릭 — 해당 grid 토글. */
  onToggle: (mode: OverlayMode) => void;
}

const monthLabelOf = (d: Date, locale?: string) =>
  new Intl.DateTimeFormat(locale, { month: "long" }).format(d);
const yearLabelOf = (d: Date) => String(d.getFullYear());

/**
 * 달력 헤더의 **연/월 독립 버튼**("2026 ▾" / "July ▾", 연-월 순). 각 버튼은 한 번 클릭으로 자기
 * grid(연/월)를 열고, 열리면 캐럿이 ▲로 뒤집혀 일/월/년 3단계 전환(특히 월→년 진입 가능)을 드러낸다.
 * 버튼은 스타일드 button — 추후 IconButton이 커스텀 콘텐츠를 지원하면 쉽게 교체 가능하도록 격리한다.
 */
export const CalendarNav = ({
  paneView,
  locale,
  monthsOpen,
  yearsOpen,
  onToggle,
}: CalendarNavProps) => (
  <MonthYearNav>
    <LabelButton
      type="button"
      onClick={() => onToggle("years")}
      aria-label={`${yearLabelOf(paneView)} — select year`}
      aria-expanded={yearsOpen}
    >
      {yearLabelOf(paneView)}
      <Caret aria-hidden="true" $up={yearsOpen}>
        <ChevronIcon />
      </Caret>
    </LabelButton>
    <LabelButton
      type="button"
      onClick={() => onToggle("months")}
      aria-label={`${monthLabelOf(paneView, locale)} — select month`}
      aria-expanded={monthsOpen}
    >
      {monthLabelOf(paneView, locale)}
      <Caret aria-hidden="true" $up={monthsOpen}>
        <ChevronIcon />
      </Caret>
    </LabelButton>
  </MonthYearNav>
);

// 월/연 독립 버튼 묶음.
const MonthYearNav = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
`;

// "연 ▾" 또는 "월 ▾" — 라벨 + 캐럿. 클릭 시 각자 연/월 grid 토글(열리면 캐럿 ▲).
const LabelButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  border: none;
  background: transparent;
  padding: 0.25rem 0.5rem;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  font: inherit;
  font-size: ${({ theme }) => theme.typography.size.body};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  color: ${({ theme }) => theme.color.text.default};
  cursor: pointer;
  ${controlTransition("background-color")}

  &:hover {
    background: ${({ theme }) => theme.color.bg.surfaceRaised};
  }
  &:focus-visible {
    ${controlFocusRing()}
  }
`;

// 캐럿 — 자기 grid가 열리면(▲) 180° 회전해 "여기서 더 펼쳐진 상태"를 표시.
const Caret = styled.span<{ $up?: boolean }>`
  display: inline-flex;
  font-size: 0.75em;
  color: ${({ theme }) => theme.color.text.subtle};
  transition: transform 120ms ease;
  transform: ${({ $up }) => ($up ? "rotate(180deg)" : "none")};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
